import { useCallback, useEffect, useState } from 'react';
import { ALERT_TYPES } from '@vigie/shared/constants';
import { flotte, réglages } from '../api.js';
import type { Réglages as RéglagesData, SiteFlotte, TestCanal, TestDépôt } from '../api.js';

interface Props {
  organizationId: string;
  rôle: string;
  onRetour: () => void;
}

const LIBELLÉS_ÉVÉNEMENT: Record<string, string> = {
  site_down: 'Site en panne',
  disk_high: 'Disque presque plein',
  backup_failed: 'Sauvegarde en échec',
  update_failed: 'Mise à jour en échec',
  major_available: 'Version majeure disponible',
  ssh_host_key_mismatch: 'Empreinte SSH modifiée',
};

const DÉPÔT_VIDE = {
  endpoint: '',
  bucket: '',
  region: 'eu-west-1',
  prefix: '',
  accessKey: '',
  secretKey: '',
};

const CANAL_VIDE = {
  name: '',
  url: '',
  hmacSecret: '',
  siteId: '',
  events: [] as string[],
  format: 'vigie' as 'vigie' | 'slack',
};

/**
 * Réglages d'une organisation : dépôt de sauvegarde, canaux d'alerte, fenêtre de mise à jour.
 *
 * Sans dépôt, aucune sauvegarde n'est possible ; sans canal, les alertes sont enregistrées mais
 * n'atteignent personne. L'écran met donc l'accent sur la vérification plutôt que sur la saisie :
 * chaque réglage peut être éprouvé pour de vrai — un aller-retour complet sur le dépôt, un envoi
 * signé vers le canal — parce qu'un réglage enregistré sans être essayé ne se découvre faux qu'à la
 * première alerte, c'est-à-dire trop tard.
 */
export function Réglages({ organizationId, rôle, onRetour }: Props) {
  const peutModifier = rôle === 'admin';

  const [données, setDonnées] = useState<RéglagesData | null>(null);
  const [sites, setSites] = useState<SiteFlotte[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);

  const [dépôt, setDépôt] = useState({ ...DÉPÔT_VIDE });
  const [testDépôt, setTestDépôt] = useState<TestDépôt | null>(null);
  const [canal, setCanal] = useState({ ...CANAL_VIDE });
  const [testsCanaux, setTestsCanaux] = useState<Record<string, TestCanal>>({});
  const [fenêtre, setFenêtre] = useState({ startHour: 3, endHour: 5, timeZone: 'Europe/Paris' });

  const charger = useCallback(async () => {
    try {
      const [réponse, parc] = await Promise.all([
        réglages.lire(organizationId),
        flotte.lister(organizationId),
      ]);
      if (réponse.status >= 400) {
        setErreur(réponse.data.error ?? 'Réglages indisponibles');
        return;
      }
      setDonnées(réponse.data);
      setFenêtre({
        startHour: réponse.data.updateWindow.startHour,
        endHour: réponse.data.updateWindow.endHour,
        timeZone: réponse.data.updateWindow.timeZone,
      });
      if (parc.status < 400) setSites(parc.data.sites);
    } catch {
      setErreur('Service injoignable');
    }
  }, [organizationId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  async function enregistrerDépôt() {
    setEnCours('dépôt');
    setErreur(null);
    setTestDépôt(null);
    try {
      const { status, data } = await réglages.enregistrerDépôt(organizationId, {
        scope: 'organization',
        ...dépôt,
      });
      if (status >= 400) setErreur(data.error ?? 'Enregistrement impossible');
      else {
        setDépôt({ ...DÉPÔT_VIDE });
        await charger();
      }
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function testerDépôt(id: string) {
    setEnCours(`test-${id}`);
    setTestDépôt(null);
    try {
      const { data } = await réglages.testerDépôt(organizationId, id);
      setTestDépôt(data);
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function supprimerDépôt(id: string) {
    setEnCours(`suppr-${id}`);
    try {
      await réglages.supprimerDépôt(organizationId, id);
      setTestDépôt(null);
      await charger();
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function créerCanal() {
    setEnCours('canal');
    setErreur(null);
    try {
      const { status, data } = await réglages.créerCanal(organizationId, {
        name: canal.name,
        url: canal.url,
        format: canal.format,
        enabled: true,
        ...(canal.hmacSecret === '' ? {} : { hmacSecret: canal.hmacSecret }),
        ...(canal.siteId === '' ? {} : { siteId: canal.siteId }),
        ...(canal.events.length === 0 ? {} : { events: canal.events }),
      });
      if (status >= 400) setErreur(data.error ?? 'Création impossible');
      else {
        setCanal({ ...CANAL_VIDE });
        await charger();
      }
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function testerCanal(id: string) {
    setEnCours(`canal-${id}`);
    try {
      const { data } = await réglages.testerCanal(organizationId, id);
      setTestsCanaux((précédents) => ({ ...précédents, [id]: data }));
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function basculerCanal(id: string, actif: boolean) {
    const existant = données?.webhooks.find((c) => c.id === id);
    if (existant === undefined) return;
    setEnCours(`bascule-${id}`);
    try {
      // Le secret n'est pas transmis : son absence le laisse en place, ce qui est exactement ce
      // qu'on veut en changeant seulement l'activation.
      await réglages.modifierCanal(organizationId, id, {
        name: existant.name,
        url: existant.url,
        format: existant.format,
        enabled: actif,
        ...(existant.siteId === null ? {} : { siteId: existant.siteId }),
        ...(existant.events === null ? {} : { events: existant.events }),
      });
      await charger();
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function enregistrerFenêtre() {
    setEnCours('fenêtre');
    setErreur(null);
    try {
      const { status, data } = await réglages.enregistrerFenêtre(organizationId, {
        name: données?.updateWindow.name ?? '',
        ...fenêtre,
      });
      if (status >= 400) setErreur(data.error ?? 'Enregistrement impossible');
      else await charger();
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  if (erreur !== null && données === null) return <p className="erreur">{erreur}</p>;
  if (données === null) return <p className="aide">Chargement des réglages…</p>;

  const nomDuSite = (id: string | null) =>
    id === null ? 'toute l’organisation' : (sites.find((s) => s.id === id)?.name ?? id);

  return (
    <section className="carte">
      <button type="button" className="lien" onClick={onRetour}>
        ← Retour
      </button>
      <h2>Réglages</h2>
      {erreur !== null && <p className="erreur">{erreur}</p>}
      {!peutModifier && (
        <p className="avertissement">
          Lecture seule : ces réglages sont réservés aux administratrices et administrateurs.
        </p>
      )}

      <h3>Dépôt de sauvegarde</h3>
      {données.storage.length === 0 ? (
        <p className={données.globalStorageConfigured ? 'aide' : 'avertissement'}>
          {données.globalStorageConfigured
            ? 'Aucun dépôt propre à cette organisation : les sauvegardes utilisent le dépôt global de l’instance.'
            : 'Aucun dépôt configuré : aucune sauvegarde n’est possible pour cette organisation.'}
        </p>
      ) : (
        <ul className="liste-depots">
          {données.storage.map((entrée) => (
            <li key={entrée.id}>
              <div className="version-entete">
                <strong>{entrée.bucket}</strong>
                <span className="etiquette">
                  {entrée.scope === 'organization'
                    ? 'toute l’organisation'
                    : nomDuSite(entrée.siteId)}
                </span>
                <span className="aide">
                  {entrée.endpoint} · {entrée.region}
                  {entrée.prefix === '' ? '' : ` · préfixe ${entrée.prefix}`}
                </span>
              </div>
              <p className="aide">
                Clé d’accès <code>{entrée.accessKey}</code> — la clé secrète est enregistrée
                chiffrée et ne peut pas être relue.
              </p>
              {peutModifier && (
                <div className="actions actions--serrees">
                  <button
                    type="button"
                    disabled={enCours !== null}
                    onClick={() => void testerDépôt(entrée.id)}
                  >
                    {enCours === `test-${entrée.id}` ? 'Test en cours…' : 'Tester le dépôt'}
                  </button>
                  <button
                    type="button"
                    className="lien"
                    disabled={enCours !== null}
                    onClick={() => void supprimerDépôt(entrée.id)}
                  >
                    Supprimer
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {testDépôt !== null && (
        <p className={testDépôt.ok ? 'succes' : 'erreur'}>
          {testDépôt.ok
            ? testDépôt.message
            : `Échec à l’étape « ${testDépôt.step} » : ${testDépôt.message}`}
        </p>
      )}

      {peutModifier && (
        <details>
          <summary>
            {données.storage.length === 0 ? 'Configurer un dépôt' : 'Remplacer le dépôt'}
          </summary>
          <div className="formulaire">
            <label htmlFor="dépôt-endpoint">
              Point d’accès <span className="aide">(schéma compris : https://…)</span>
            </label>
            <input
              id="dépôt-endpoint"
              type="text"
              value={dépôt.endpoint}
              placeholder="https://s3.fr-par.scw.cloud"
              onChange={(é) => setDépôt({ ...dépôt, endpoint: é.target.value })}
            />

            <label htmlFor="dépôt-bucket">Bucket</label>
            <input
              id="dépôt-bucket"
              type="text"
              value={dépôt.bucket}
              onChange={(é) => setDépôt({ ...dépôt, bucket: é.target.value })}
            />

            <label htmlFor="dépôt-region">Région</label>
            <input
              id="dépôt-region"
              type="text"
              value={dépôt.region}
              onChange={(é) => setDépôt({ ...dépôt, region: é.target.value })}
            />

            <label htmlFor="dépôt-prefix">
              Préfixe <span className="aide">(facultatif, pour partager un bucket)</span>
            </label>
            <input
              id="dépôt-prefix"
              type="text"
              value={dépôt.prefix}
              onChange={(é) => setDépôt({ ...dépôt, prefix: é.target.value })}
            />

            <label htmlFor="dépôt-access">Clé d’accès</label>
            <input
              id="dépôt-access"
              type="text"
              autoComplete="off"
              value={dépôt.accessKey}
              onChange={(é) => setDépôt({ ...dépôt, accessKey: é.target.value })}
            />

            <label htmlFor="dépôt-secret">Clé secrète</label>
            <input
              id="dépôt-secret"
              type="password"
              autoComplete="new-password"
              value={dépôt.secretKey}
              onChange={(é) => setDépôt({ ...dépôt, secretKey: é.target.value })}
            />

            <p className="aide">
              Un dépôt européen est attendu : les sauvegardes contiennent l’intégralité des sites.
              Après enregistrement, teste le dépôt — le test écrit, relit puis supprime un objet, ce
              qui vérifie les trois droits dont une sauvegarde a besoin.
            </p>

            <div className="actions">
              <button
                type="button"
                disabled={
                  enCours !== null ||
                  dépôt.endpoint === '' ||
                  dépôt.bucket === '' ||
                  dépôt.accessKey === '' ||
                  dépôt.secretKey === ''
                }
                onClick={() => void enregistrerDépôt()}
              >
                {enCours === 'dépôt' ? 'Enregistrement…' : 'Enregistrer le dépôt'}
              </button>
            </div>
          </div>
        </details>
      )}

      <h3>Canaux d’alerte</h3>
      {données.webhooks.length === 0 ? (
        <p className="avertissement">
          Aucun canal : les alertes sont enregistrées et visibles dans l’interface, mais
          n’avertissent personne.
        </p>
      ) : (
        <ul className="liste-canaux">
          {données.webhooks.map((entrée) => (
            <li key={entrée.id}>
              <div className="version-entete">
                <strong>{entrée.name}</strong>
                {!entrée.enabled && <span className="etiquette etiquette--alerte">désactivé</span>}
                {entrée.format === 'slack' && <span className="etiquette">Slack</span>}
                {entrée.signed && <span className="etiquette">signé</span>}
                <span className="etiquette">{nomDuSite(entrée.siteId)}</span>
              </div>
              <p className="aide">
                {entrée.url} —{' '}
                {entrée.events === null
                  ? 'tous les événements'
                  : entrée.events.map((é) => LIBELLÉS_ÉVÉNEMENT[é] ?? é).join(', ')}
              </p>
              {peutModifier && (
                <div className="actions actions--serrees">
                  <button
                    type="button"
                    disabled={enCours !== null}
                    onClick={() => void testerCanal(entrée.id)}
                  >
                    {enCours === `canal-${entrée.id}` ? 'Envoi…' : 'Envoyer un test'}
                  </button>
                  <button
                    type="button"
                    className="lien"
                    disabled={enCours !== null}
                    onClick={() => void basculerCanal(entrée.id, !entrée.enabled)}
                  >
                    {entrée.enabled ? 'Désactiver' : 'Réactiver'}
                  </button>
                  <button
                    type="button"
                    className="lien"
                    disabled={enCours !== null}
                    onClick={() =>
                      void réglages.supprimerCanal(organizationId, entrée.id).then(charger)
                    }
                  >
                    Supprimer
                  </button>
                </div>
              )}
              {testsCanaux[entrée.id] !== undefined && (
                <p className={testsCanaux[entrée.id]!.ok ? 'succes' : 'erreur'}>
                  {testsCanaux[entrée.id]!.ok
                    ? `Reçu (HTTP ${String(testsCanaux[entrée.id]!.status)}). L’envoi de test porte l’état « resolved » pour ne réveiller personne.`
                    : `Échec : ${testsCanaux[entrée.id]!.error ?? 'sans détail'}`}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {peutModifier && (
        <details>
          <summary>Ajouter un canal</summary>
          <div className="formulaire">
            <label htmlFor="canal-nom">Nom</label>
            <input
              id="canal-nom"
              type="text"
              value={canal.name}
              placeholder="Astreinte, Mattermost, Gotify…"
              onChange={(é) => setCanal({ ...canal, name: é.target.value })}
            />

            <label htmlFor="canal-url">URL</label>
            <input
              id="canal-url"
              type="text"
              value={canal.url}
              onChange={(é) => setCanal({ ...canal, url: é.target.value })}
            />

            <label htmlFor="canal-format">Format de la charge utile</label>
            <select
              id="canal-format"
              value={canal.format}
              onChange={(é) =>
                setCanal({ ...canal, format: é.target.value === 'slack' ? 'slack' : 'vigie' })
              }
            >
              <option value="vigie">Vigie — charge utile documentée, signée</option>
              <option value="slack">Slack — webhook entrant</option>
            </select>
            <p className="aide">
              Slack n’accepte pas de JSON arbitraire : il refuse tout ce qui n’est pas son propre
              format. Choisir « Slack » fait rendre l’alerte en message lisible ; laisser « Vigie »
              sur une URL Slack donnerait un canal qui échoue à chaque alerte — donc les seuls jours
              où il sert.
            </p>

            <label htmlFor="canal-secret">
              Secret de signature{' '}
              <span className="aide">
                (facultatif : signe chaque envoi en HMAC-SHA256 dans X-Vigie-Signature)
              </span>
            </label>
            <input
              id="canal-secret"
              type="password"
              autoComplete="new-password"
              value={canal.hmacSecret}
              onChange={(é) => setCanal({ ...canal, hmacSecret: é.target.value })}
            />

            <label htmlFor="canal-site">Portée</label>
            <select
              id="canal-site"
              value={canal.siteId}
              onChange={(é) => setCanal({ ...canal, siteId: é.target.value })}
            >
              <option value="">Toute l’organisation</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>

            <fieldset className="config-groupe">
              <legend>Événements</legend>
              <p className="aide">
                Aucun choix = tous les événements. Filtrer évite le canal unique où tout finit noyé.
              </p>
              {ALERT_TYPES.map((type) => (
                <div key={type} className="case">
                  <label>
                    <input
                      type="checkbox"
                      checked={canal.events.includes(type)}
                      onChange={(é) =>
                        setCanal({
                          ...canal,
                          events: é.target.checked
                            ? [...canal.events, type]
                            : canal.events.filter((autre) => autre !== type),
                        })
                      }
                    />
                    {LIBELLÉS_ÉVÉNEMENT[type] ?? type}
                  </label>
                </div>
              ))}
            </fieldset>

            <div className="actions">
              <button
                type="button"
                disabled={enCours !== null || canal.name === '' || canal.url === ''}
                onClick={() => void créerCanal()}
              >
                {enCours === 'canal' ? 'Création…' : 'Ajouter le canal'}
              </button>
            </div>
          </div>
        </details>
      )}

      <h3>Fenêtre des mises à jour automatiques</h3>
      <p className="aide">
        Les mises à jour automatiques (politique « mineures » ou « toutes ») ne sont lancées que
        dans cette plage. Une mise à jour commencée dans la fenêtre n’est jamais interrompue à la
        fin de celle-ci ; une mise à jour qui n’y entre pas est reportée.
      </p>
      <div className="formulaire formulaire--enligne">
        <label htmlFor="fenêtre-début">De</label>
        <input
          id="fenêtre-début"
          type="number"
          min={0}
          max={23}
          value={fenêtre.startHour}
          disabled={!peutModifier}
          onChange={(é) => setFenêtre({ ...fenêtre, startHour: Number(é.target.value) })}
        />
        <label htmlFor="fenêtre-fin">à</label>
        <input
          id="fenêtre-fin"
          type="number"
          min={0}
          max={23}
          value={fenêtre.endHour}
          disabled={!peutModifier}
          onChange={(é) => setFenêtre({ ...fenêtre, endHour: Number(é.target.value) })}
        />
        <label htmlFor="fenêtre-zone">heure de</label>
        <input
          id="fenêtre-zone"
          type="text"
          value={fenêtre.timeZone}
          disabled={!peutModifier}
          onChange={(é) => setFenêtre({ ...fenêtre, timeZone: é.target.value })}
        />
        {peutModifier && (
          <button
            type="button"
            disabled={enCours !== null}
            onClick={() => void enregistrerFenêtre()}
          >
            {enCours === 'fenêtre' ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        )}
      </div>
    </section>
  );
}
